import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navigationItems } from '../data/mock';
import { Button } from './ui/button';
import { Menu, X, Briefcase, TrendingUp, Presentation } from 'lucide-react';
import { useMode } from '../contexts/ModeContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { mode, toggleMode } = useMode();
  const location = useLocation();
  const navigate = useNavigate();

  // Filter nav items based on mode
  const baseNavItems = mode === 'solve'
    ? navigationItems.filter(item => item.name !== 'Team' && item.name !== 'Investors')
    : navigationItems;

  const extendedNavItems = [
    ...baseNavItems,
    { name: 'Targets', href: '#targets' },
    { name: 'Assets', href: '#assets' }
  ];
  
  // Add mode-specific items
  const modeSpecificItems = mode === 'invest' 
    ? [{ name: 'Roadmap', href: '#roadmap' }]
    : [];
  
  const allNavItems = [...extendedNavItems, ...modeSpecificItems];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    handleScroll(); // initialise on mount so the header is never transparent over carried-over content
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  // Logo returns the user to the NEW site. On /assets (reached from the new site) it goes
  // back to the corresponding section via ?ref=; on /legacy it stays within the old site.
  const handleLogoClick = () => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/legacy') {
      scrollToSection('#hero');
      return;
    }
    const ref = new URLSearchParams(location.search).get('ref');
    navigate('/' + (ref ? `#${ref}` : ''));
  };

  // On legacy sub-pages (e.g. /assets) the in-page section nav has no targets, so route the
  // legacy header controls to the corresponding NEW-site sections. On /legacy keep old behaviour.
  const NEW_SITE_MAP = {
    Technology: '#technology', Products: '#pulsbeat', Markets: '#applications',
    Targets: '#applications', Assets: '#ip', Roadmap: '#strategic',
    Investors: '#strategic', Team: '#company',
  };
  const handleNav = (item) => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/legacy') { scrollToSection(item.href); return; }
    navigate('/' + (NEW_SITE_MAP[item.name] || ''));
  };
  const handleCta = () => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/legacy') { scrollToSection('#contact'); return; }
    navigate('/#pilot');
  };

  return (
    <motion.header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-slate-900/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.button
            onClick={handleLogoClick}
            data-testid="legacy-nav-logo"
            title="Back to General Harmonics"
            className="text-2xl font-bold text-white cursor-pointer"
            whileHover={{ scale: 1.05 }}
          >
            <span className="text-cyan-400">General </span>
            <span className="text-blue-400">Harmonics</span>
          </motion.button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {allNavItems.map((item) => (
              <motion.button
                key={item.name}
                onClick={() => handleNav(item)}
                className="text-gray-300 hover:text-cyan-400 transition-colors duration-200 text-sm"
                whileHover={{ y: -2 }}
              >
                {item.name}
              </motion.button>
            ))}

            {/* MSML Keynote — discreet entry */}
            <Link
              to="/msml-keynote"
              data-testid="header-msml-keynote-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/25 px-3 py-1.5 text-xs text-cyan-300/90 transition-colors hover:border-cyan-400/60 hover:text-cyan-200"
            >
              <Presentation size={13} />
              Keynote
            </Link>
            
            {/* Mode Toggle */}
            <div className="flex items-center gap-1 bg-slate-800 rounded-full p-1 border border-cyan-400/30">
              <button
                onClick={() => mode === 'invest' && toggleMode()}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1 ${
                  mode === 'solve'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <Briefcase size={14} />
                Solve
              </button>
              <button
                onClick={() => mode === 'solve' && toggleMode()}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1 ${
                  mode === 'invest'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <TrendingUp size={14} />
                Invest
              </button>
            </div>

            <Button 
              className={mode === 'solve' 
                ? "bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                : "bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
              }
              onClick={handleCta}
            >
              {mode === 'solve' ? 'Get Started' : 'Invest Now'}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <motion.div
            className="md:hidden mt-4 pb-4 border-t border-gray-700"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            {allNavItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNav(item)}
                className="block w-full text-left py-2 text-gray-300 hover:text-cyan-400 transition-colors"
              >
                {item.name}
              </button>
            ))}

            {/* MSML Keynote — discreet entry (mobile) */}
            <Link
              to="/msml-keynote"
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="header-msml-keynote-link-mobile"
              className="mt-1 inline-flex items-center gap-2 py-2 text-cyan-300 hover:text-cyan-200 transition-colors"
            >
              <Presentation size={15} />
              Keynote
            </Link>
            
            {/* Mobile Mode Toggle */}
            <div className="flex gap-2 my-4">
              <button
                onClick={() => mode === 'invest' && toggleMode()}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                  mode === 'solve'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                    : 'bg-slate-800 text-gray-400'
                }`}
              >
                <Briefcase size={16} />
                Solve
              </button>
              <button
                onClick={() => mode === 'solve' && toggleMode()}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                  mode === 'invest'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                    : 'bg-slate-800 text-gray-400'
                }`}
              >
                <TrendingUp size={16} />
                Invest
              </button>
            </div>

            <Button 
              className={`w-full ${
                mode === 'solve' 
                  ? "bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                  : "bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
              }`}
              onClick={handleCta}
            >
              {mode === 'solve' ? 'Get Started' : 'Invest Now'}
            </Button>
          </motion.div>
        )}
      </nav>
    </motion.header>
  );
};

export default Header;